import SubscriptionPageSkeleton from '@/components/settings/subscription/SubscriptionPageSkeleton';
import { BillingSettingsSection } from '@/components/settings/subscription/BillingSettingsSection';
import { CurrentPlanFees } from '@/components/settings/subscription/CurrentPlanFees';
import { OrganizationCurrentPlanSection } from '@/components/settings/subscription/OrganizationCurrentPlanSection';
import { ShopifyFreeBillingSection } from '@/components/settings/subscription/ShopifyFreeBillingSection';
import { OrganizationClosureSection } from '@/components/settings/subscription/OrganizationClosureSection';
import { ContactHelpLink } from '@/components/ContactHelpLink';
import PlanBadge from '@/components/subscription/PlanBadge';
import { useBillingContextQuery } from '@/hooks/queries/use-billing-context-query';
import { readBillingContext } from '@/libs/billing-access';
import { BillingTaxProfileForm } from '@/pages/onboarding/billing/BillingTaxProfileForm';
import { NoticePanel } from '@fluenceur/ui';
import { PageHeader } from '@/components/ui';
import { toaster } from '@/components/ui/toaster';
import { usePlansQuery } from '@/hooks/queries/use-plans-query';
import { useAuth } from '@/contexts/auth-context';
import { useShopCurrency } from '@/hooks/queries/use-shop-currency';
import { useSubscriptionPriceDropQuery } from '@/hooks/queries/use-subscription-price-drop';
import { useSubscriptionQuery } from '@/hooks/queries/use-subscription-query';
import { useSuccessFeeMigrationQuery } from '@/hooks/queries/use-success-fee-migration';
import { formatPlanPrice } from '@/libs/utils/format-plan-price';
import { CurrentPlanChangeNotice } from '@/components/subscription/plan-choice/PlanChangeNotice';
import { usePageTitleStore } from '@/stores/page-title';
import { usePaywall } from '@/hooks/use-paywall';
import { Badge, Box, Button, Heading, Text, VStack } from '@chakra-ui/react';
import type { CurrentSubscription } from '@fluenceur/types';
import { PlanTier } from '@fluenceur/types';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SettingsSection from './components/SettingsSection';
import { SwitchToFreeSection } from './components/switch-to-free/SwitchToFreeSection';
import { PartnerSuccessFeesSection } from './components/partner-success-fees/PartnerSuccessFeesSection';
import { formatSettingsDate } from './components/format-settings-date';
import { SubscriptionPriceDropCard } from './components/subscription-price-drop/SubscriptionPriceDropCard';
import { useSubscriptionPriceDropReturn } from './components/subscription-price-drop/use-subscription-price-drop-return';
import { PlanChangeFrozenNotice } from './components/success-fee-migration/PlanChangeFrozenNotice';
import {
  shouldRenderSuccessFeeMigration,
  SuccessFeeMigrationCard,
} from './components/success-fee-migration/SuccessFeeMigrationCard';
import { useSuccessFeeMigrationReturn } from './components/success-fee-migration/use-success-fee-migration-return';

const NoSubscriptionState: React.FC = () => {
  const { t } = useTranslation();
  const { openUpgradeModal } = usePaywall();

  return (
    <VStack w="full" gap={6} py={20} align="center">
      <VStack gapY={2} align="center">
        <Heading size="lg">{t('settings.subscription.noSubscription.title')}</Heading>
        <Text fontSize="sm" color="fg.muted" textAlign="center">
          {t('settings.subscription.noSubscription.message')}
        </Text>
      </VStack>
      <Button
        bg="gray.900"
        color="white"
        _hover={{ bg: 'gray.800' }}
        size="lg"
        mt={4}
        onClick={() => openUpgradeModal('generic', PlanTier.ESSENTIAL)}
      >
        {t('settings.subscription.noSubscription.action')}
      </Button>
    </VStack>
  );
};

const getStatusColorPalette = (
  status: CurrentSubscription['status']
): 'green' | 'orange' | 'red' | 'gray' => {
  switch (status) {
    case 'active':
      return 'green';
    case 'pending':
      return 'orange';
    case 'expired':
      return 'red';
    default:
      return 'gray';
  }
};

const SubscriptionPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPartnerBrand = user?.currentBrand?.accountType === 'partner';
  const currentBrandId = user?.currentBrand?.id ?? null;
  const billingContext = useBillingContextQuery({ enabled: !isPartnerBrand });
  const scheduledFree = billingContext.data?.scheduledFreePlan;
  const canConfirmFree =
    !!billingContext.data?.capabilities?.canReturnToFree &&
    billingContext.isSuccess &&
    !billingContext.isFetching &&
    !billingContext.error;
  const shopCurrency = useShopCurrency();
  const {
    data: subscription,
    isLoading,
    error,
  } = useSubscriptionQuery({
    enabled: !isPartnerBrand,
    scopeKey: currentBrandId,
  });
  const { data: plansData } = usePlansQuery({
    enabled: !isPartnerBrand,
    currency: shopCurrency,
  });
  const { data: migration } = useSuccessFeeMigrationQuery({
    enabled: !isPartnerBrand,
    realtime: !isPartnerBrand,
  });
  const isMigrationRefetching = useSuccessFeeMigrationReturn();
  const { data: priceDrop } = useSubscriptionPriceDropQuery({ enabled: !isPartnerBrand });
  const isPriceDropReturning = useSubscriptionPriceDropReturn();
  const { openPlans } = usePaywall();

  const setTitle = usePageTitleStore((state) => state.setTitle);

  const pendingPriceDrop = isPartnerBrand ? null : (priceDrop?.offer ?? null);
  // The card is held back while the post-Shopify refetch runs, so an offer the
  // merchant has just approved never flashes.
  const priceDropOffer = isPriceDropReturning ? null : pendingPriceDrop;

  const cataloguePlanPrice = plansData?.plans.find(
    (plan) => plan.id === subscription?.planId
  )?.price;
  // A subscription billed above the catalogue shows what it is billed today,
  // or the plan section would already read the lower price the card below
  // asks the merchant to approve.
  const currentPlanPrice = pendingPriceDrop
    ? { amount: pendingPriceDrop.currentAmount, currencyCode: pendingPriceDrop.currency }
    : cataloguePlanPrice;
  const showSuccessFeeMigration = !isPartnerBrand && shouldRenderSuccessFeeMigration(migration);

  const stripeContext = readBillingContext(billingContext.data);
  const isShopifyFreeContext =
    stripeContext?.channel === 'shopify' &&
    stripeContext.effectiveTier === PlanTier.FREE &&
    !!stripeContext.shopifyFreeApproval;
  const displayBillingContext =
    stripeContext && billingContext.isFetching
      ? {
          ...stripeContext,
          capabilities: {
            ...stripeContext.capabilities,
            canCommitPaidPlan: false,
            canCloseOrganization: false,
            canReturnToFree: false,
          },
        }
      : stripeContext;

  useEffect(() => {
    setTitle(t(isPartnerBrand ? 'settings.billing' : 'settings.subscription.title'));
    return () => setTitle('');
  }, [isPartnerBrand, setTitle, t]);

  useEffect(() => {
    if (error) {
      toaster.error({
        title: t('common.error'),
        description: error.message,
      });
    }
  }, [error, t]);

  if (
    !isPartnerBrand &&
    billingContext.isSuccess &&
    !billingContext.error &&
    !!stripeContext &&
    (stripeContext?.channel === 'stripe' ||
      stripeContext?.channel === 'manual' ||
      isShopifyFreeContext ||
      (stripeContext?.channel === 'none' && stripeContext.effectiveTier === PlanTier.FREE)) &&
    !!user?.id &&
    stripeContext.brandId === user?.currentBrandId &&
    stripeContext.organizationId === user?.currentBrand?.organizationId
  ) {
    return (
      <Box maxW="1000px" w="full">
        <PageHeader
          title={t('settings.subscription.title')}
          description={t('settings.subscription.description')}
          titleSize="xl"
          marginBottom={4}
        />
        {stripeContext.planChange && (
          <Box mb={6}>
            <CurrentPlanChangeNotice context={displayBillingContext ?? stripeContext} />
          </Box>
        )}
        <OrganizationCurrentPlanSection context={displayBillingContext ?? stripeContext} />
        {stripeContext.channel === 'shopify' && (
          <ShopifyFreeBillingSection
            key={`${stripeContext.organizationId}:${stripeContext.brandId}`}
            context={displayBillingContext ?? stripeContext}
            mutationsDisabled={billingContext.isFetching}
          />
        )}
        {stripeContext.channel === 'stripe' ? (
          <>
            <BillingSettingsSection
              context={displayBillingContext ?? stripeContext}
              mutationsDisabled={billingContext.isFetching}
            />
            {stripeContext.capabilities.canReadBilling && (
              <SettingsSection title={t('settings.invoices.title')}>
                <Button asChild variant="outline">
                  <Link to="/settings/invoices">{t('settings.invoices.viewHistory')}</Link>
                </Button>
              </SettingsSection>
            )}
            <Box py={{ base: 6, md: 8 }}>
              <BillingTaxProfileForm context={displayBillingContext ?? stripeContext} />
            </Box>
          </>
        ) : stripeContext.channel === 'manual' ? (
          <SettingsSection
            title={t('settings.subscription.billingManagement.manualTitle')}
            description={t('settings.subscription.billingManagement.manualDescription')}
            isLast
          >
            <ContactHelpLink />
          </SettingsSection>
        ) : null}
        <OrganizationClosureSection
          key={`${user.id}:${stripeContext.organizationId}:${stripeContext.brandId}:${user.currentBrandRole}`}
          context={displayBillingContext ?? stripeContext}
        />
      </Box>
    );
  }

  if (!isPartnerBrand && (billingContext.isPending || billingContext.isFetching)) {
    return <SubscriptionPageSkeleton />;
  }

  const billingContextMismatch =
    !!stripeContext &&
    (stripeContext.brandId !== user?.currentBrandId ||
      stripeContext.organizationId !== user?.currentBrand?.organizationId);
  if (!isPartnerBrand && (billingContext.error || billingContextMismatch)) {
    return (
      <Box maxW="1000px" w="full">
        <PageHeader
          title={t('settings.subscription.title')}
          description={t('settings.subscription.description')}
          titleSize="xl"
          marginBottom={4}
        />
        <NoticePanel
          tone="warning"
          mb={6}
          title={t('settings.subscription.accessReadError')}
          actions={
            <Button variant="outline" size="sm" onClick={() => void billingContext.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      </Box>
    );
  }

  if (isLoading) {
    return <SubscriptionPageSkeleton />;
  }

  if (!subscription && !isPartnerBrand) {
    return <NoSubscriptionState />;
  }

  const formatDate = (dateString: string | null): string =>
    formatSettingsDate(dateString, t('common.locale'));

  const isCancelled = !!subscription?.cancelledAt;
  const canChangePlan =
    !!subscription &&
    subscription.planId !== 'free-beta' &&
    !isCancelled &&
    billingContext.data?.capabilities?.canCommitPaidPlan !== false;
  // Leaving for the free plan is offered from an active paid plan only.
  const paidPlanKey = subscription?.planId.startsWith('growth')
    ? 'growth'
    : subscription?.planId.startsWith('essential')
      ? 'essential'
      : null;
  const canSwitchToFree = canChangePlan && subscription?.status === 'active' && !!paidPlanKey;
  // A paid period runs to its end after the switch; a trial ends at once.
  const isInTrial =
    !!subscription?.trialEndsAt && new Date(subscription.trialEndsAt).getTime() > Date.now();
  const paidUntil = isInTrial ? null : (subscription?.currentPeriodEnd ?? null);

  return (
    <Box maxW="1000px" w="full">
      {!isPartnerBrand && (
        <PageHeader
          title={t('settings.subscription.title')}
          description={t('settings.subscription.description')}
          titleSize="xl"
          marginBottom={4}
        />
      )}
      {billingContext.error && (
        <NoticePanel
          tone="warning"
          mb={6}
          title={t('settings.subscription.accessReadError')}
          actions={
            <Button variant="outline" size="sm" onClick={() => void billingContext.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      )}
      {scheduledFree && (
        <NoticePanel
          tone="info"
          mb={6}
          title={t(
            billingContext.data?.accessSource === 'legacy_brand_bypass'
              ? 'settings.subscription.switchToFree.changes.subscriptionUntil'
              : 'settings.subscription.switchToFree.scheduled.effective',
            {
              plan: paidPlanKey ? t(`pricing.plan.${paidPlanKey}.name`) : subscription?.planName,
              date: formatDate(scheduledFree.effectiveAt),
            }
          )}
        >
          {billingContext.data?.accessSource === 'legacy_brand_bypass'
            ? t('settings.subscription.switchToFree.retainedAccess.description')
            : scheduledFree.keptProgram
              ? t('settings.subscription.switchToFree.scheduled.keptProgram', {
                  program: scheduledFree.keptProgram.name,
                })
              : t('settings.subscription.switchToFree.scheduled.keptProgramUnavailable')}
        </NoticePanel>
      )}
      {subscription ? (
        <SettingsSection
          title={t('settings.subscription.currentPlan.title')}
          description={t('settings.subscription.currentPlan.description')}
          actions={
            canChangePlan ? (
              <Box>
                <Button
                  variant="plain"
                  size="md"
                  colorPalette="primary"
                  px={0}
                  fontWeight="medium"
                  justifyContent="flex-start"
                  onClick={() => {
                    openPlans(subscription.planId.startsWith('growth') ? 'growth' : 'essential');
                  }}
                >
                  {t('settings.subscription.currentPlan.manageButton')}
                </Button>
                {migration?.planChangeFrozen && <PlanChangeFrozenNotice />}
              </Box>
            ) : undefined
          }
          isLast={
            !priceDropOffer && !showSuccessFeeMigration && stripeContext?.channel !== 'shopify'
          }
        >
          <VStack align="flex-start" gap={2}>
            <PlanBadge planId={subscription.planId} size="sm" />
            <Text fontSize="xl" fontWeight="medium">
              {currentPlanPrice
                ? `${formatPlanPrice(
                    currentPlanPrice.amount,
                    currentPlanPrice.currencyCode,
                    t('common.locale')
                  )}${t('pricing.plan.perMonth')}`
                : ''}
            </Text>
            <CurrentPlanFees
              planKey={paidPlanKey ?? subscription.planId}
              currencyCode={currentPlanPrice?.currencyCode}
            />

            {!isCancelled && subscription.currentPeriodEnd && (
              <Text fontSize="sm" color="fg.muted">
                {t('settings.subscription.billing.autoRenewOn', {
                  date: formatDate(subscription.currentPeriodEnd),
                })}
              </Text>
            )}
            {isCancelled && subscription.currentPeriodEnd && (
              <Text fontSize="sm" color="orange.fg" fontWeight="medium">
                {t('settings.subscription.billing.expiresOn', {
                  date: formatDate(subscription.currentPeriodEnd),
                })}
              </Text>
            )}

            {subscription.status === 'pending' && (
              <Badge
                size="lg"
                colorPalette={getStatusColorPalette(subscription.status)}
                variant="subtle"
              >
                {t(`settings.subscription.status.${subscription.status}`)}
              </Badge>
            )}

            {canSwitchToFree && paidPlanKey && (
              <Box pt={4}>
                <SwitchToFreeSection
                  planKey={paidPlanKey}
                  paidUntil={paidUntil}
                  accessSource={billingContext.data?.accessSource}
                  canConfirm={canConfirmFree}
                  programs={billingContext.data?.freePlanPrograms}
                />
              </Box>
            )}
          </VStack>
        </SettingsSection>
      ) : null}
      <SubscriptionPriceDropCard offer={priceDropOffer} isLast={!showSuccessFeeMigration} />
      {showSuccessFeeMigration && (
        <SuccessFeeMigrationCard status={migration} isRefetching={isMigrationRefetching} />
      )}
      {isPartnerBrand ? <PartnerSuccessFeesSection /> : null}
      {!isPartnerBrand && stripeContext?.channel === 'shopify' && (
        <SettingsSection
          title={t('settings.subscription.billingManagement.invoicesTitle')}
          description={t('settings.subscription.billingManagement.shopifyDescription')}
          isLast
        >
          <Button asChild variant="outline">
            <a
              href="https://admin.shopify.com/settings/billing"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('settings.subscription.billingManagement.shopifyInvoices')}
            </a>
          </Button>
        </SettingsSection>
      )}
      {!isPartnerBrand && stripeContext && (
        <OrganizationClosureSection
          key={`${user?.id}:${stripeContext.organizationId}:${stripeContext.brandId}:${user?.currentBrandRole}`}
          context={stripeContext}
        />
      )}
    </Box>
  );
};

export default SubscriptionPage;
